import { Feather, AudioLines, Leaf, type LucideIcon } from 'lucide-react'

export type BlogCategory =
  | 'soul-thoughts'
  | 'energy-tuning'
  | 'lifestyle-basket'

export interface BlogPost {
  slug: string
  title: string
  excerpt: string
  category: BlogCategory
  date: string
  image: string
  externalUrl: string
}

export interface Category {
  id: BlogCategory
  name: string
  description: string
  icon: LucideIcon
}

export const categories: Category[] = [
  {
    id: 'soul-thoughts',
    name: 'Soul Thoughts',
    description: 'Personal reflections, mindful living, inner journeys.',
    icon: Feather,
  },
  {
    id: 'energy-tuning',
    name: 'Energy Tuning & Meditation',
    description:
      'Biofield Tuning, sound work, meditation practices, and insights on energy.',
    icon: AudioLines,
  },
  {
    id: 'lifestyle-basket',
    name: 'Lifestyle Basket',
    description: 'Simple joys of nature, nourishment, and creativity.',
    icon: Leaf,
  },
]

// Fallback used when an image fails to load
export const FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?auto=format&fit=crop&q=80'

export const blogPosts: BlogPost[] = [
  // Lifestyle Basket
  {
    slug: 'healing-powers-of-garlic',
    title: 'The healing powers of garlic',
    excerpt:
      "A humble bulb with extraordinary gifts. Exploring garlic's ancient role as a natural healer — from immunity to vitality, the simple medicines that grow in our own kitchens.",
    category: 'lifestyle-basket',
    date: '2023-08-24',
    image:
      'https://cdn.magicpatterns.com/uploads/ssXA1xckY3LRbAnmSgbgNq/garlic.png',
    externalUrl:
      'https://milangelassoulgarden.com/2023/08/24/healing-powers-of-garlic/',
  },
  {
    slug: 'spending-time-in-forests-shinrin-yoku',
    title: 'Spending time in forests — Shinrin-yoku',
    excerpt:
      'The Japanese practice of forest bathing as medicine for body and soul. How simply being among the trees restores the nervous system and reconnects us to something larger.',
    category: 'lifestyle-basket',
    date: '2023-04-15',
    image:
      'https://images.unsplash.com/photo-1448375240586-882707db888b?auto=format&fit=crop&q=80&w=1200',
    externalUrl:
      'https://milangelassoulgarden.com/2023/04/15/spending-time-in-forests-shinrin-joku/',
  },

  // Soul Thoughts
  {
    slug: 'how-to-move-on-when-life-has-pulled-you-down',
    title: 'How to move on when life has pulled you down',
    excerpt:
      'There are seasons that ask everything of us. A gentle exploration of resilience, surrender, and the quiet courage it takes to keep walking forward.',
    category: 'soul-thoughts',
    date: '2023-06-14',
    image:
      'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?auto=format&fit=crop&q=80&w=1200',
    externalUrl:
      'https://milangelassoulgarden.com/2023/06/14/how-to-move-on-when-life-has-pulled-you-down/',
  },
  {
    slug: 'who-are-we-in-reality',
    title: 'Who are we in reality?',
    excerpt:
      'Beneath the roles we play and the stories we carry, who is the one observing it all? Reflections on identity, presence, and the deeper self.',
    category: 'soul-thoughts',
    date: '2023-02-28',
    image:
      'https://images.unsplash.com/photo-1502082553048-f009c37129b9?auto=format&fit=crop&q=80&w=1200',
    externalUrl:
      'https://milangelassoulgarden.com/2023/02/28/who-are-we-in-reality/',
  },
  {
    slug: 'on-life',
    title: 'On life',
    excerpt:
      'A meditation on the ordinary miracle of being here. Small notes on what it means to truly live — not just exist — within the days we are given.',
    category: 'soul-thoughts',
    date: '2023-01-22',
    image:
      'https://images.unsplash.com/photo-1470770841072-f978cf4d019e?auto=format&fit=crop&q=80&w=1200',
    externalUrl: 'https://milangelassoulgarden.com/2023/01/22/on-life/',
  },
  {
    slug: 'on-new-years-resolutions',
    title: "On New Year's resolutions",
    excerpt:
      'Beyond the lists and ambitions — what if the new year invited a softer kind of intention? Thoughts on beginnings that honor where we actually are.',
    category: 'soul-thoughts',
    date: '2022-12-30',
    image:
      'https://images.unsplash.com/photo-1483794344563-d27a8d18014e?auto=format&fit=crop&q=80&w=1200',
    externalUrl:
      'https://milangelassoulgarden.com/2022/12/30/on-new-years-resolutions/',
  },
  {
    slug: 'appreciating-what-matters',
    title: 'Appreciating what matters',
    excerpt:
      'When we pause long enough to notice, the most important things have often been here all along. A reflection on gratitude as a way of seeing.',
    category: 'soul-thoughts',
    date: '2022-11-21',
    image:
      'https://images.unsplash.com/photo-1418065460487-3e41a6c84dc5?auto=format&fit=crop&q=80&w=1200',
    externalUrl:
      'https://milangelassoulgarden.com/2022/11/21/appreciating-what-matters/',
  },
  {
    slug: 'the-mighty-silence',
    title: 'The mighty silence',
    excerpt:
      'Silence is not absence — it is its own kind of fullness. On the quiet that holds us, teaches us, and reveals what noise cannot.',
    category: 'soul-thoughts',
    date: '2022-09-15',
    image:
      'https://images.unsplash.com/photo-1518495973542-4542c06a5843?auto=format&fit=crop&q=80&w=1200',
    externalUrl:
      'https://milangelassoulgarden.com/2022/09/15/the-mighty-silence/',
  },
  {
    slug: 'the-power-of-thought',
    title: 'The power of thought',
    excerpt:
      'Our inner conversation shapes the world we inhabit. A gentle look at how thoughts become beliefs, beliefs become reality — and how to soften both.',
    category: 'soul-thoughts',
    date: '2022-07-01',
    image:
      'https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&q=80&w=1200',
    externalUrl:
      'https://milangelassoulgarden.com/2022/07/01/the-power-of-thought/',
  },

  // Energy Tuning & Meditation
  {
    slug: 'old-habits-die-hard',
    title: 'Old habits die hard',
    excerpt:
      'Why patterns persist, and how energy work meets the body where conscious effort cannot. A look at the deeper layers of change through Biofield Tuning.',
    category: 'energy-tuning',
    date: '2022-08-20',
    image:
      'https://images.unsplash.com/photo-1528319725582-ddc096101511?auto=format&fit=crop&q=80&w=1200',
    externalUrl:
      'https://milangelassoulgarden.com/2022/08/20/old-habits-die-hard/',
  },
]
